import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MagnifyingGlass, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";

export default function Akten() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [akten, setAkten] = useState([]);
  const [mandanten, setMandanten] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("alle");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ mandant_id: "", mandant_name: "", unfalldatum: "", unfallort: "", art: "unfall" });

  const load = async () => {
    const [a, m] = await Promise.all([api.get("/akten"), api.get("/mandanten")]);
    setAkten(a.data);
    setMandanten(m.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open dialog when navigated with ?new=1&art=hfg|owi
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const artParam = searchParams.get("art");
      if (artParam === "owi") {
        setForm(f => ({ ...f, art: "owi" }));
      } else if (artParam === "hfg") {
        setForm(f => ({ ...f, art: "unfall" }));
      }
      setOpen(true);
      searchParams.delete("new");
      searchParams.delete("art");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.mandant_id) {
        const m = mandanten.find(x => x.id === payload.mandant_id);
        if (m) payload.mandant_name = `${m.vorname} ${m.nachname}`;
      }
      const r = await api.post("/akten", payload);
      toast.success(`Akte ${r.data.aktenzeichen} angelegt`);
      setOpen(false);
      setForm({ mandant_id: "", mandant_name: "", unfalldatum: "", unfallort: "", art: "unfall" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const filtered = akten.filter(a => {
    if (status !== "alle" && a.status !== status) return false;
    if (search && !`${a.aktenzeichen} ${a.mandant_name || ""} ${a.unfallort || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-8" data-testid="akten-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">Akten</p>
          <h1 className="font-serif text-4xl tracking-tight">{akten.length} Akte{akten.length === 1 ? "" : "n"}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="new-akte-btn"><Plus size={16} className="mr-2" /> Akte anlegen</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Neue Akte anlegen</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="overline">Art</Label>
                <Select value={form.art} onValueChange={v => setForm(f => ({ ...f, art: v }))}>
                  <SelectTrigger data-testid="new-akte-art"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unfall">HFG (Haftpflichtgutachten)</SelectItem>
                    <SelectItem value="owi">OWi-Verfahren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Mandant</Label>
                <Select value={form.mandant_id || "none"} onValueChange={v => setForm(f => ({ ...f, mandant_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="new-akte-mandant"><SelectValue placeholder="optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Kein Mandant —</SelectItem>
                    {mandanten.map(m => <SelectItem key={m.id} value={m.id}>{m.vorname} {m.nachname}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!form.mandant_id && (
                <div>
                  <Label className="overline">Mandantenname (optional)</Label>
                  <Input value={form.mandant_name} onChange={e => setForm(f => ({ ...f, mandant_name: e.target.value }))} placeholder="z.B. Max Müller" data-testid="new-akte-name" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline">Unfalldatum</Label>
                  <Input type="date" value={form.unfalldatum} onChange={e => setForm(f => ({ ...f, unfalldatum: e.target.value }))} data-testid="new-akte-date" />
                </div>
                <div>
                  <Label className="overline">Unfallort</Label>
                  <Input value={form.unfallort} onChange={e => setForm(f => ({ ...f, unfallort: e.target.value }))} placeholder="z.B. Berlin, A100" data-testid="new-akte-ort" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="new-akte-submit">{saving ? "Anlegen..." : "Akte anlegen"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-64">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input className="pl-9" placeholder="Aktenzeichen, Mandant, Ort..." value={search} onChange={e => setSearch(e.target.value)} data-testid="akten-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="akten-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle Status</SelectItem>
            <SelectItem value="offen">Offen</SelectItem>
            <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
            <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
          <FolderOpen size={40} weight="duotone" className="mx-auto text-[#9CA3AF]" />
          <p className="font-serif text-2xl mt-4">Keine Akten gefunden</p>
          <p className="text-[#4B5563] text-sm mt-2">Legen Sie Ihre erste Akte an, um zu starten.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F3F4F6]">
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-[#4B5563]">
                <th className="px-5 py-3">Aktenzeichen</th>
                <th className="px-5 py-3">Mandant</th>
                <th className="px-5 py-3">Unfall</th>
                <th className="px-5 py-3 text-right">Streitwert</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-t border-[#E5E7EB] hover:bg-[#FAFAFA]">
                  <td className="px-5 py-4">
                    <Link to={`/app/akten/${a.id}`} className="font-mono text-sm text-[#1A2238] hover:text-[#D32F2F]" data-testid={`akte-row-${a.aktenzeichen}`}>
                      {a.aktenzeichen}
                    </Link>
                    <div className="overline text-[#9CA3AF] mt-1">{a.art === "owi" ? "OWi" : "HFG"}</div>
                  </td>
                  <td className="px-5 py-4">{a.mandant_name || "—"}</td>
                  <td className="px-5 py-4 text-sm text-[#4B5563]">
                    {a.unfalldatum ? new Date(a.unfalldatum).toLocaleDateString("de-DE") : "—"} {a.unfallort ? `· ${a.unfallort}` : ""}
                  </td>
                  <td className="px-5 py-4 text-right font-mono">
                    {a.bezifferung?.streitwert
                      ? new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(a.bezifferung.streitwert)
                      : "—"}
                  </td>
                  <td className="px-5 py-4"><StatusBadge status={a.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
